import { createEffect, createSignal, For, Show, type Component } from 'solid-js';

import { isBoundAlertSound } from '../audio/cues';
import {
  DEFAULT_FILTER_TEXT,
  formatDiagnostic,
  groundBlindConditions,
  type BlindCondition,
} from '../game/filter';
import { KEYBINDS } from '../game/keyboard';
import type { GameStore } from '../state/store';

/**
 * The in-game loot filter editor.
 *
 * Minimal on purpose: a textarea, an Apply, a Revert, and — the part that matters — the
 * parser's diagnostics rendered with their line, column and caret. The filter core's
 * second invariant is that **parsing fails loudly and locatably**, and a panel that
 * swallowed the caret would make that invariant unobservable from inside the game, which
 * is the only place a player will ever meet it.
 *
 * ## What it shows besides errors
 *
 * Three kinds of thing can be wrong with a filter, and only one of them is a syntax
 * error:
 *
 * - **Errors** stop the filter compiling. The last good filter stays live, so a typo
 *   costs the new rules and nothing else; the panel says so rather than leaving the
 *   player to wonder whether their file is running.
 * - **Conditions that can never fire on a floor drop** — `ItemLevel`, `Stat`, `Class`,
 *   `BaseType`, `AffixCount`. The server withholds those until an item is in the bag, so
 *   a rule using one is silently inert on the ground. `groundBlindConditions` is exactly
 *   this list, and it exists so a player is not left inferring it from an empty screen.
 * - **`PlayAlertSound` ids with no cue bound.** Every sound here is synthesised, and only
 *   a handful of ids have a synth behind them. An unbound id stays silent — which is the
 *   right behaviour and an awful thing to debug without being told.
 *
 * The draft lives in a local signal, which is fine: it is a value being edited, not the
 * panel's open state. **Open state is `store.menu()`**, because this covers the viewport
 * and a locally-tracked overlay lets clicks on its backdrop fall through to the canvas
 * and walk the player around behind it.
 */
export const FilterEditor: Component<{ store: GameStore }> = (props) => {
  const open = () => props.store.menu() === 'filter';
  const [draft, setDraft] = createSignal(props.store.filterText());

  // Re-seed the draft from the committed text whenever the panel opens. Without this a
  // player who edited, closed without applying, and reopened would be looking at their
  // abandoned text as though it were live — and the diagnostics beneath it would be
  // about something else entirely.
  createEffect(() => {
    if (open()) setDraft(props.store.filterText());
  });

  const dirty = () => draft() !== props.store.filterText();
  const diagnostics = () => props.store.filterDiagnostics();
  const blind = () => groundBlindConditions(props.store.filter());
  const unboundSounds = () => {
    const ids = new Set<number>();
    for (const rule of props.store.filter().rules) {
      const id = rule.style.soundId;
      if (id !== undefined && !isBoundAlertSound(id)) ids.add(id);
    }
    return [...ids].sort((a, b) => a - b);
  };

  return (
    <Show when={open()}>
      <div
        class="sheet hud-interactive"
        data-testid="filter-editor"
        role="dialog"
        aria-modal="true"
        aria-label="Loot filter"
      >
        <div class="plate plate--riveted sheet__panel">
          <header class="sheet__head">
            <h2 class="sheet__title">Loot Filter</h2>
            <button
              type="button"
              class="sheet__close"
              data-testid="filter-close"
              onClick={() => props.store.closeMenu()}
              aria-label="Close"
            >
              ✕
            </button>
          </header>

          <p class="filter-editor__lede">
            Rules are tried top down and the first match wins, unless it says{' '}
            <code>Continue</code>. Tier 1 and Tier 2 drops are what{' '}
            <kbd>{KEYBINDS.vacuum.toUpperCase()}</kbd> sweeps up. A filter changes what you
            are <em>shown</em> — never what dropped, and never what you may pick up.
          </p>

          <textarea
            class="filter-editor__text"
            data-testid="filter-text"
            aria-label="Filter source"
            spellcheck={false}
            rows={18}
            value={draft()}
            onInput={(e) => setDraft(e.currentTarget.value)}
          />

          <div class="filter-editor__actions">
            <button
              type="button"
              class="sigil-button filter-editor__apply"
              data-testid="filter-apply"
              onClick={() => props.store.setFilterText(draft())}
            >
              <span>{dirty() ? 'Apply' : 'Applied'}</span>
            </button>
            <button
              type="button"
              class="sigil-button filter-editor__revert"
              data-testid="filter-revert"
              onClick={() => {
                props.store.resetFilter();
                setDraft(DEFAULT_FILTER_TEXT);
              }}
            >
              <span>Revert to starter</span>
            </button>
            <span class="filter-editor__count" data-testid="filter-rule-count">
              {props.store.filter().rules.length} rule
              {props.store.filter().rules.length === 1 ? '' : 's'} live
            </span>
          </div>

          <Show when={diagnostics().length > 0}>
            <section class="filter-editor__errors" data-testid="filter-errors">
              <h3 class="filter-editor__heading">
                {diagnostics().length} problem{diagnostics().length === 1 ? '' : 's'} — the
                previous filter is still running
              </h3>
              <For each={diagnostics()}>
                {(d) => (
                  <pre class="filter-editor__caret" data-testid={`filter-error-${d.line}`}>
                    {formatDiagnostic(d)}
                  </pre>
                )}
              </For>
            </section>
          </Show>

          <Show when={diagnostics().length === 0 && !dirty()}>
            <p class="filter-editor__ok" data-testid="filter-ok">
              Parsed cleanly. This is the filter running now.
            </p>
          </Show>

          <Show when={blind().length > 0}>
            <section class="filter-editor__warnings" data-testid="filter-blind">
              <h3 class="filter-editor__heading">Cannot match a drop on the floor</h3>
              <For each={blind()}>{(b) => <BlindLine warning={b} />}</For>
            </section>
          </Show>

          <Show when={unboundSounds().length > 0}>
            <section class="filter-editor__warnings" data-testid="filter-sounds">
              <h3 class="filter-editor__heading">No sound bound</h3>
              <p class="filter-editor__warning">
                PlayAlertSound {unboundSounds().join(', ')} — every sound here is
                synthesised and these ids have no synth behind them, so they play nothing.
                Bound ids are 0 (silence), 1, 2, 3 and 4.
              </p>
            </section>
          </Show>
        </div>
      </div>
    </Show>
  );
};

/** One condition that will never fire on a ground drop. */
const BlindLine: Component<{ warning: BlindCondition }> = (props) => (
  <p class="filter-editor__warning" data-testid={`filter-blind-${props.warning.line}`}>
    line {props.warning.line}: {props.warning.message}
  </p>
);
